import { ScreenHeader } from '@/components/layout/screen-header';
import { TextInput } from '@/components/ui/text-input';
import { useAuth } from '@/hooks';
import { useAppTheme } from '@/providers/theme-provider';
import { useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';
import {
    ActivityIndicator,
    FlatList,
    Pressable,
    Text,
    View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { AdminUserRow } from '../components/admin-user-row';
import {
    useAdminBanUser,
    useAdminListUsers,
    useAdminSetRole,
    useAdminUnbanUser,
} from '../hooks/use-admin-users';

const PER_PAGE = 20;

export function AdminUsersScreen() {
  const { user: currentUser } = useAuth();
  const theme = useAppTheme();
  const router = useRouter();
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, isFetching } = useAdminListUsers({
    page,
    perPage: PER_PAGE,
    searchValue: searchValue || undefined,
  });

  const setRoleMutation = useAdminSetRole();
  const banMutation = useAdminBanUser();
  const unbanMutation = useAdminUnbanUser();

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PER_PAGE);

  const handleSearch = useCallback(() => {
    setSearchValue(searchInput.trim());
    setPage(1);
  }, [searchInput]);

  const handleSetRole = useCallback(
    (userId: string, role: 'user' | 'admin') => {
      setRoleMutation.mutate({ userId, role });
    },
    [setRoleMutation]
  );

  const handleBan = useCallback(
    (userId: string) => {
      banMutation.mutate({ userId });
    },
    [banMutation]
  );

  const handleUnban = useCallback(
    (userId: string) => {
      unbanMutation.mutate({ userId });
    },
    [unbanMutation]
  );

  const handleViewProfile = useCallback(
    (userId: string, username?: string | null) => {
      router.push(`/(tabs)/profile/${username || userId}`);
    },
    [router]
  );

  const renderItem = useCallback(
    ({ item }: { item: any }) => (
      <AdminUserRow
        user={item}
        currentUserId={currentUser?.id}
        onSetRole={handleSetRole}
        onBan={handleBan}
        onUnban={handleUnban}
        onViewProfile={handleViewProfile}
      />
    ),
    [handleSetRole, handleBan, handleUnban, handleViewProfile, currentUser?.id]
  );

  return (
    <SafeAreaView className="flex-1 bg-background">
      <ScreenHeader title="User Management" showBackButton />

      <View style={{ paddingHorizontal: 16, paddingBottom: 12 }}>
        <View style={{ flexDirection: 'row' }}>
          <TextInput
            size="sm"
            value={searchInput}
            onChangeText={setSearchInput}
            onSubmitEditing={handleSearch}
            placeholder="Search by email..."
            returnKeyType="search"
            autoCapitalize="none"
            autoCorrect={false}
            inputContainerStyle={{ flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRightWidth: 0 }}
          />
          <Pressable
            onPress={handleSearch}
            accessibilityRole="button"
            accessibilityLabel="Search users"
            className="bg-primary"
            style={{
              paddingHorizontal: 16,
              justifyContent: 'center',
              borderTopRightRadius: 10,
              borderBottomRightRadius: 10,
            }}
          >
            <Text className="text-background" style={{ fontSize: 14, fontWeight: '600' }}>
              Search
            </Text>
          </Pressable>
        </View>
      </View>

      <FlatList
        data={users}
        keyExtractor={(item: any) => item.id}
        renderItem={renderItem}
        contentContainerStyle={{ paddingBottom: 24 }}
        ListEmptyComponent={
          isLoading ? (
            <View style={{ paddingVertical: 40, alignItems: 'center' }}>
              <ActivityIndicator color={theme.colors.primary} />
            </View>
          ) : (
            <View style={{ paddingVertical: 40, alignItems: 'center' }}>
              <Text className="text-muted-foreground" style={{ fontSize: 15 }}>No users found</Text>
            </View>
          )
        }
      />

      {totalPages > 1 && (
        <View
          className="border-t border-border"
          style={{
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            paddingVertical: 12,
            gap: 16,
          }}
        >
          <Pressable
            disabled={page <= 1}
            onPress={() => setPage((p) => Math.max(1, p - 1))}
            accessibilityRole="button"
            accessibilityLabel="Previous page"
            style={{ opacity: page <= 1 ? 0.4 : 1 }}
          >
            <Text className="text-primary" style={{ fontSize: 15, fontWeight: '600' }}>
              Previous
            </Text>
          </Pressable>
          <Text className="text-muted-foreground" style={{ fontSize: 14 }}>
            {page} / {totalPages}
          </Text>
          <Pressable
            disabled={page >= totalPages}
            onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
            accessibilityRole="button"
            accessibilityLabel="Next page"
            style={{ opacity: page >= totalPages ? 0.4 : 1 }}
          >
            <Text className="text-primary" style={{ fontSize: 15, fontWeight: '600' }}>
              Next
            </Text>
          </Pressable>
          {isFetching && <ActivityIndicator size="small" color={theme.colors.primary} />}
        </View>
      )}
    </SafeAreaView>
  );
}
